import { useState } from "react";
import axios from "axios";
import { RefreshCw, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { getAuthHeaders } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

export default function ChangePasswordModal({ open, onOpenChange }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowPass(false);
  };

  const handleOpenChange = (next) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success("Password changed successfully");
      handleOpenChange(false);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to change password";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock size={18} /> Change Password</DialogTitle>
          <DialogDescription className="text-zinc-500">Update your account password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Current Password</label>
              <Input
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                type={showPass ? "text" : "password"}
                placeholder="********"
                className="bg-zinc-800 border-zinc-700 text-white"
                required
                data-testid="change-password-current"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">New Password</label>
              <Input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                type={showPass ? "text" : "password"}
                placeholder="********"
                className="bg-zinc-800 border-zinc-700 text-white"
                required
                data-testid="change-password-new"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Confirm New Password</label>
              <div className="relative">
                <Input
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type={showPass ? "text" : "password"}
                  placeholder="********"
                  className="bg-zinc-800 border-zinc-700 text-white pr-10"
                  required
                  data-testid="change-password-confirm"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="change-password-submit">
              {loading && <RefreshCw className="animate-spin mr-2" size={16} />}
              Update Password
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
